/**
 * AdCreativePanel — the actual Meta ad behind a row (Joey, Oct 21 2026).
 * Video ads: player + download + copyable transcript. Static ads: full image + download.
 * Plus the ad's primary text / headline / CTA. Data: /api/ads/creative (session auth), media streamed by
 * /api/ads/creative/media (Meta's file URLs expire, so the server fetches a fresh one each time).
 */
import { useEffect, useState } from "react";

interface CreativeData {
  adId: string; adName: string; kind: "video" | "image" | "carousel" | "unknown";
  body: string; title: string; description: string; cta: string; link: string;
  videos: Array<{ index: number; videoId: string; transcript: string | null }>;
  transcript: string | null;
  images: Array<{ index: number; width: number | null; height: number | null }>;
}

async function copyText(text: string) {
  if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return; }
  const ta = document.createElement("textarea");
  ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
  document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
}

function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" onClick={async () => { try { await copyText(text); setDone(true); setTimeout(() => setDone(false), 1200); } catch { /* ignore */ } }}
      className="px-2.5 py-1 rounded-md border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors">
      {done ? "Copied" : label}
    </button>
  );
}

const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors";

function MetaPreview({ adId }: { adId: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let off = false;
    fetch(`/api/ads/creative/preview?adId=${encodeURIComponent(adId)}`).then((r) => r.json()).then((j) => { if (off) return; if (j.success && j.data?.src) setSrc(j.data.src); else setErr(j.error || "No preview"); }).catch((e) => !off && setErr(String(e)));
    return () => { off = true; };
  }, [adId]);
  if (err) return <p className="text-xs text-muted-foreground">Meta preview unavailable: {err}</p>;
  if (!src) return <p className="text-xs text-muted-foreground">Loading Meta's preview…</p>;
  return <iframe src={src} title="Meta ad preview" width={335} height={560} className="rounded-xl border border-border bg-white" allow="autoplay" />;
}

export function AdCreativePanel({ adId, adName }: { adId?: string; adName?: string }) {
  const [data, setData] = useState<CreativeData | null>(null);
  const [error, setError] = useState("");
  const [videoFailed, setVideoFailed] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [aspect, setAspect] = useState("9 / 16");
  useEffect(() => {
    let off = false;
    setData(null); setError("");
    const q = adId ? `adId=${encodeURIComponent(adId)}` : `adName=${encodeURIComponent(adName || "")}`;
    fetch(`/api/ads/creative?${q}`).then(async (r) => {
      const j = await r.json().catch(() => ({}));
      if (off) return;
      if (j.success) setData(j.data); else setError(j.error || `HTTP ${r.status}`);
    }).catch((e) => !off && setError(String(e)));
    return () => { off = true; };
  }, [adId, adName]);

  if (error) return <div className="px-6 py-4 text-sm text-muted-foreground">Couldn't load this ad from Meta: {error}</div>;
  if (!data) return <div className="px-6 py-4"><div className="h-40 w-64 bg-secondary/30 rounded-xl animate-pulse" /></div>;

  const media = (type: string, i: number, dl = false) => `/api/ads/creative/media?adId=${encodeURIComponent(data.adId)}&type=${type}&i=${i}${dl ? "&download=1" : ""}`;
  const video = data.videos[0];
  const transcript = data.transcript ?? video?.transcript ?? null;

  return (
    <div className="sticky left-0 max-w-[1040px] px-6 py-5 bg-secondary/10 border-t border-border/40">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="shrink-0 space-y-2">
          {video && !videoFailed && (
            <video controls preload="metadata" playsInline poster={media("thumb", 0)} src={media("video", 0)}
              onError={() => setVideoFailed(true)}
              onLoadedMetadata={(e) => { const v = e.currentTarget; if (v.videoWidth && v.videoHeight) setAspect(`${v.videoWidth} / ${v.videoHeight}`); }}
              style={{ aspectRatio: aspect }}
              className="block w-[300px] h-auto max-h-[540px] rounded-xl border border-border bg-black object-contain" />
          )}
          {video && videoFailed && <MetaPreview adId={data.adId} />}
          {!video && data.images.length > 0 && (
            <div className={`flex gap-3 ${data.images.length > 1 ? "overflow-x-auto max-w-[640px]" : ""}`}>
              {data.images.map((im) => (
                <div key={im.index} className="space-y-1.5 shrink-0">
                  <a href={media("image", im.index)} target="_blank" rel="noreferrer">
                    <img src={media("image", im.index)} alt={data.adName} className="w-[300px] rounded-xl border border-border bg-white" loading="lazy" />
                  </a>
                  <a className={btn} href={media("image", im.index, true)}>Download image{data.images.length > 1 ? ` ${im.index + 1}` : ""}</a>
                </div>
              ))}
            </div>
          )}
          {!video && data.images.length === 0 && <MetaPreview adId={data.adId} />}
          {video && videoFailed && (
            <p className="max-w-[335px] text-[11px] text-muted-foreground">Meta won't hand over this video's file to the dashboard (permission), so it plays in Meta's own preview above. Download it from Ads Manager.</p>
          )}
          {video && !videoFailed && (
            <div className="flex flex-wrap gap-2">
              <a className={btn} href={media("video", 0, true)}>Download video</a>
              <a className={btn} href={media("video", 0)} target="_blank" rel="noreferrer">Open full size</a>
            </div>
          )}
          {((video && !videoFailed) || data.images.length > 0) && !showPreview && (
            <button type="button" className="text-[11px] text-muted-foreground underline" onClick={() => setShowPreview(true)}>Show Meta's feed preview</button>
          )}
          {showPreview && ((video && !videoFailed) || data.images.length > 0) && <MetaPreview adId={data.adId} />}
        </div>

        <div className="flex-1 min-w-0 space-y-4 text-sm">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <span className="px-2 py-0.5 rounded-md bg-secondary/60 font-semibold">{data.kind === "image" ? "Static" : data.kind}</span>
            <span className="truncate" title={data.adName}>{data.adName}</span>
          </div>

          {video && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Transcript</h4>
                {transcript && <CopyButton text={transcript} label="Copy transcript" />}
              </div>
              {transcript
                ? <div className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-xl border border-border bg-card p-3 text-[13px] leading-relaxed">{transcript}</div>
                : <p className="text-xs text-muted-foreground">{transcript === "" ? "No speech in this video." : videoFailed ? "No transcript: Meta doesn't share this video's file." : "Transcript not generated yet for this video."}</p>}
            </div>
          )}

          {data.body && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Primary text</h4>
                <CopyButton text={data.body} />
              </div>
              <p className="whitespace-pre-wrap rounded-xl border border-border bg-card p-3 text-[13px]">{data.body}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            {data.title && <div><div className="text-muted-foreground uppercase tracking-wide mb-0.5">Headline</div><div className="font-medium">{data.title}</div></div>}
            {data.cta && <div><div className="text-muted-foreground uppercase tracking-wide mb-0.5">CTA</div><div className="font-medium">{data.cta}</div></div>}
            {data.link && <div className="min-w-0"><div className="text-muted-foreground uppercase tracking-wide mb-0.5">Link</div><a href={data.link} target="_blank" rel="noreferrer" className="font-medium break-all hover:text-[var(--brand-primary)]">{data.link}</a></div>}
          </div>
          {data.description && <p className="text-xs text-muted-foreground">{data.description}</p>}
        </div>
      </div>
    </div>
  );
}

/** Small ▸/▾ toggle used in table rows. */
export function CreativeToggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} title={open ? "Hide ad" : "Show the ad"}
      className={`mr-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border text-xs transition-colors ${open ? "bg-[var(--brand-primary)] text-white border-transparent" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"}`}>
      {open ? "▾" : "▸"}
    </button>
  );
}
